import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, FlaskConical, Factory, Wrench,
  CheckCircle, Users, Globe, Award, Zap,
  HeartPulse, Car, UtensilsCrossed, Microscope, Flame, Cpu, Rocket,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import VideoWidget from '../components/VideoWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Animated counter ─── */
function AnimatedNumber({ value, suffix = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const spring = useSpring(0, { stiffness: 60, damping: 18 });
  const display = useTransform(spring, v => Math.round(v));

  useEffect(() => { if (isInView) spring.set(value); }, [isInView, spring, value]);

  return (
    <span ref={ref} className="tabular-nums">
      <motion.span>{display}</motion.span>{suffix}
    </span>
  );
}

/* ─── Scroll-reveal wrapper ─── */
const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ─── Data ─── */
const HERO_BG = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=2000&q=85';

const stats = [
  { value: 15, suffix: '+', label: 'Years Experience', icon: Award },
  { value: 9, suffix: '', label: 'Plants in India', icon: Factory },
  { value: 100, suffix: '+', label: 'Expert Staff', icon: Users },
  { value: 400, suffix: ' bar', label: 'Max Fill Pressure', icon: Zap },
];

const services = [
  {
    num: '01', icon: FlaskConical,
    title: 'Gases & Liquids',
    desc: 'Industrial, medical, mixture, cryogenic, and specialty gases manufactured to world-class purity standards for every sector.',
    link: '/products',
  },
  {
    num: '02', icon: Factory,
    title: 'Air Separation Plants',
    desc: 'Turn-key delivery of ASUs with liquid plants from 50 to 500 TPD, gas plants up to 1000 TPD. Complete erection and commissioning.',
    link: '/services',
  },
  {
    num: '03', icon: Wrench,
    title: 'Engineering & Maintenance',
    desc: 'Hydrostatic testing, cryogenic vessel maintenance, gas analysis, offshore purging, and full lifecycle support.',
    link: '/services',
  },
];

const processSteps = [
  { step: '01', title: 'Consultation & Analysis', desc: 'Understanding your gas requirements, consumption patterns, and operational constraints.' },
  { step: '02', title: 'Engineering & Design', desc: 'Custom engineering and system design tailored to your plant layout and specifications.' },
  { step: '03', title: 'Manufacturing & QC', desc: 'Precision manufacturing with complete quality control at every stage of production.' },
  { step: '04', title: 'Installation & Commissioning', desc: 'On-site erection, commissioning, and testing by our certified engineering team.' },
  { step: '05', title: 'Maintenance & Support', desc: 'Ongoing maintenance contracts, emergency support, and periodic safety testing.' },
];

const LOGO_SLOTS = 6;

// ─── Feature flags — flip to true when client confirms ───────────────────────
const SHOW_CLIENT_LOGOS = false;   // "Trusted Across Industries" marquee
const SHOW_TEAM_SECTION = false;   // "Our People / The Experts Behind Every Solution"
// ─────────────────────────────────────────────────────────────────────────────

const industries = [
  { icon: HeartPulse, label: 'Medical' },
  { icon: Factory, label: 'Industrial' },
  { icon: Car, label: 'Automobile' },
  { icon: UtensilsCrossed, label: 'Food & Beverage' },
  { icon: Flame, label: 'Oil & Gas' },
  { icon: Microscope, label: 'Research & Lab' },
  { icon: Cpu, label: 'Electronics' },
  { icon: Globe, label: 'Export' },
  { icon: Rocket, label: 'Space Center' },
];

const team = [
  { name: 'Rajesh Kumar',  role: 'Founder & CEO',        expertise: '25+ years in industrial gases',    img: 'https://images.pexels.com/photos/32845669/pexels-photo-32845669.jpeg?auto=compress&cs=tinysrgb&w=500' },
  { name: 'Priya Sharma',  role: 'Technical Director',   expertise: 'Air separation technology',        img: 'https://images.pexels.com/photos/32845660/pexels-photo-32845660.jpeg?auto=compress&cs=tinysrgb&w=500' },
  { name: 'Arun Patel',    role: 'Head of Operations',   expertise: 'Plant operations & logistics',     img: 'https://images.pexels.com/photos/32845669/pexels-photo-32845669.jpeg?auto=compress&cs=tinysrgb&w=500' },
  { name: 'Deepa Nair',    role: 'Quality Control Head', expertise: 'ISO quality management',           img: 'https://images.pexels.com/photos/32845660/pexels-photo-32845660.jpeg?auto=compress&cs=tinysrgb&w=500' },
  { name: 'Suresh Rajan',  role: 'Sales Director',       expertise: 'B2B industrial solutions',         img: 'https://images.pexels.com/photos/32845669/pexels-photo-32845669.jpeg?auto=compress&cs=tinysrgb&w=500' },
];

/* ═══════════════════════════════════════════════════ */
export default function HomePage() {
  const [posts, setPosts] = useState([]);
  const [teamIdx, setTeamIdx] = useState(0);

  const visibleCards = 3;
  const canPrev = teamIdx > 0;
  const canNext = teamIdx < team.length - visibleCards;

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* ═══════════ 1. HERO ═══════════ */}
      <section className="relative min-h-screen flex flex-col" data-testid="hero-section">
        {/* BG */}
        <div className="absolute inset-0 overflow-hidden">
          <img src={HERO_BG} alt="Industrial facility" className="w-full h-full object-cover scale-105" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#181818]/95 via-[#0A0A0A]/75 to-[#0A0A0A]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#181818]/80 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative flex-1 flex items-center pt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 w-full">
            <div className="max-w-4xl">
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center gap-3 mb-8" data-testid="hero-tag">
                  <span className="block w-10 h-px bg-[#CC2222]" />
                  <span className="text-[#CC2222] text-xs font-bold uppercase tracking-[0.3em]">Energy for Engineering</span>
                </div>

                <h1 className="font-heading font-black tracking-tighter leading-[0.95] mb-8" data-testid="hero-headline">
                  <span className="block text-[clamp(3rem,8vw,5.5rem)] text-white/90">Your</span>
                  <span className="block text-[clamp(3rem,8vw,5.5rem)] text-white">Industrial & Medical</span>
                  <span className="block text-[clamp(3rem,8vw,5.5rem)] text-[#CC2222]">Gas Solutions</span>
                </h1>

                <p className="text-white/70 text-lg md:text-xl leading-relaxed mb-2 max-w-2xl font-medium">
                  Manufacturing, trading, importing and exporting industrial and medical gases to corporations and institutions across the globe.
                </p>
                <p className="text-white/45 text-base leading-relaxed mb-10 max-w-xl">
                  All-in-one gas solutions provider from planning and manufacturing to installation and long-term support.
                </p>

                <div className="flex flex-wrap gap-4" data-testid="hero-ctas">
                  <Link
                    to="/products"
                    data-testid="hero-explore-btn"
                    className="group inline-flex items-center gap-2.5 bg-[#CC2222] text-white px-8 py-4 font-bold text-sm uppercase tracking-wider hover:bg-[#A81C1C] transition-all duration-200"
                  >
                    Explore Products
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid="hero-contact-btn"
                    className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 font-bold text-sm uppercase tracking-wider hover:bg-white/10 hover:border-white/60 transition-all duration-200"
                  >
                    Enquire Now
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative border-t border-white/10 bg-black/50 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
              {stats.map(({ value, suffix, label, icon: Icon }) => (
                <div key={label} className="py-7 px-6 text-center" data-testid={`hero-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="text-3xl md:text-4xl font-black text-white font-heading mb-1 tracking-tight">
                    <AnimatedNumber value={value} suffix={suffix} />
                  </div>
                  <div className="text-[11px] text-white/40 uppercase tracking-[0.18em] font-semibold">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 2. CLIENT LOGOS — hidden until client confirms ═══════════ */}
      {SHOW_CLIENT_LOGOS && (
      <section className="py-8 bg-white border-b border-gray-100" data-testid="client-logos-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-gray-400 text-center">Trusted Across Industries</p>
        </div>
        <div className="overflow-hidden relative">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          <div
            className="flex gap-6"
            style={{
              width: 'max-content',
              animation: 'logosMarquee 22s linear infinite',
            }}
          >
            {[...Array(LOGO_SLOTS * 2)].map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-36 h-16 border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1.5 hover:border-[#CC2222]/30 transition-colors rounded-sm"
              >
                <div className="w-9 h-6 bg-gray-100 rounded" />
                <span className="text-[9px] uppercase tracking-wider text-gray-300 font-semibold">Client Logo</span>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @keyframes logosMarquee {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </section>
      )}

      {/* ═══════════ 3. SERVICES ═══════════ */}
      <section className="py-28 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end">
            <Reveal className="lg:col-span-7">
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">What We Do</span>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[#111111] font-heading leading-tight">
                Comprehensive Gas<br />Solutions for Every Need
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5 lg:text-right">
              <Link
                to="/services"
                data-testid="services-all-btn"
                className="inline-flex items-center gap-2 text-[#111111] font-bold text-sm uppercase tracking-wider hover:text-[#CC2222] group transition-colors"
              >
                All Services
                <ArrowUpRight size={16} className="group-hover:rotate-12 transition-transform" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {services.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <Link
                  to={s.link}
                  data-testid={`service-card-${i}`}
                  className="group bg-white border border-gray-200 p-8 flex flex-col hover:border-[#CC2222] hover:shadow-lg transition-all duration-300 h-full"
                >
                  <div className="flex items-start justify-between mb-6">
                    <span className="text-[#CC2222] text-xs font-black tracking-widest opacity-60">{s.num}</span>
                    <div className="w-10 h-10 bg-[#F5F5F5] group-hover:bg-[#CC2222] flex items-center justify-center transition-colors duration-300">
                      <s.icon size={20} className="text-[#111111] group-hover:text-white transition-colors duration-300" />
                    </div>
                  </div>
                  <h3 className="text-xl font-black text-[#111111] font-heading mb-3 group-hover:text-[#CC2222] transition-colors">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-6">{s.desc}</p>
                  <div className="flex items-center gap-2 text-[#CC2222] text-sm font-bold uppercase tracking-wide">
                    Learn More <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ 4. STATS / ABOUT (DARK) ═══════════ */}
      <section className="py-28 bg-[#181818]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">About Seva Gases</span>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading leading-tight mb-6">
                Built on 15 Years<br />of Engineering Trust
              </h2>
              <p className="text-white/50 text-base leading-relaxed mb-5">
                Seva Gases is one of India's leading energy companies involved in manufacturing, trading, importing and exporting industrial and medical gases to corporations and institutions across the globe.
              </p>
              <p className="text-white/50 text-base leading-relaxed mb-10">
                With 9 plants, 100+ experts, and ASU capabilities up to 1000 TPD, we are India's most capable all-in-one gas solutions provider. From planning to long-term support, we deliver end-to-end.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/about" data-testid="about-learn-more-btn" className="inline-flex items-center gap-2 bg-[#CC2222] text-white px-7 py-3.5 font-bold text-sm uppercase tracking-wider hover:bg-[#A81C1C] transition-colors group">
                  Our Story <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-3.5 font-bold text-sm uppercase tracking-wider hover:bg-white/10 transition-colors">
                  Contact Us
                </Link>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 gap-px bg-white/5">
              {[
                { value: 15, suffix: '+', label: 'Years in Operation', sub: 'Since 2009' },
                { value: 9, suffix: '', label: 'Manufacturing Plants', sub: 'Across India' },
                { value: 100, suffix: '+', label: 'Expert Professionals', sub: 'Nationwide' },
                { value: 1000, suffix: ' TPD', label: 'Max Gas Plant Capacity', sub: 'Air Separation' },
              ].map(({ value, suffix, label, sub }, i) => (
                <Reveal key={label} delay={i * 0.08} className="bg-[#202020] p-8">
                  <div className="text-4xl font-black text-white font-heading mb-2 tracking-tight">
                    <AnimatedNumber value={value} suffix={suffix} />
                  </div>
                  <div className="text-white/80 text-sm font-bold mb-1">{label}</div>
                  <div className="text-white/30 text-xs uppercase tracking-wider">{sub}</div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 5. PROCESS / END-TO-END ═══════════ */}
      <section className="py-28 bg-[#202020]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">Our Approach</span>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading leading-tight">
              End-to-End Gas Solutions<br />
              <span className="text-white/40">From Planning to Execution</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-white/5">
            {processSteps.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.07} className="bg-[#202020] p-7 group hover:bg-[#CC2222]/10 transition-colors duration-300 border border-white/5 hover:border-[#CC2222]/30">
                <div className="text-[#CC2222] text-3xl font-black font-heading mb-4 opacity-60">{p.step}</div>
                <h3 className="text-white font-bold text-base font-heading mb-3 leading-tight group-hover:text-[#CC2222] transition-colors">{p.title}</h3>
                <p className="text-white/35 text-xs leading-relaxed">{p.desc}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3} className="mt-12 text-center">
            <Link to="/services" data-testid="process-cta-btn" className="inline-flex items-center gap-2 border border-white/20 text-white/70 px-8 py-4 font-bold text-sm uppercase tracking-wider hover:border-white/60 hover:text-white transition-all">
              Explore All Services <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ═══════════ 6. INDUSTRIES ═══════════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-12">
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">Industries We Serve</span>
            <h2 className="text-4xl font-black tracking-tight text-[#111111] font-heading">Powering Every Sector</h2>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-1">
            {industries.map((ind, i) => (
              <Reveal key={ind.label} delay={i * 0.05} className="group bg-[#F5F5F5] hover:bg-[#CC2222] p-5 text-center transition-all duration-300 cursor-default" data-testid={`industry-card-${ind.label.toLowerCase()}`}>
                <ind.icon size={22} className="text-[#CC2222] group-hover:text-white transition-colors mx-auto mb-3" />
                <p className="text-[11px] font-bold text-gray-700 group-hover:text-white transition-colors uppercase tracking-wide leading-tight">{ind.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ 7. TEAM — hidden until client confirms ═══════════ */}
      {SHOW_TEAM_SECTION && (
      <section className="py-28 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-14">
            <Reveal>
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">Our People</span>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[#111111] font-heading">The Experts Behind<br />Every Solution</h2>
            </Reveal>
            <div className="hidden md:flex gap-2">
              <button
                onClick={() => setTeamIdx(i => Math.max(0, i - 1))}
                disabled={!canPrev}
                data-testid="team-prev-btn"
                className="w-11 h-11 border border-gray-300 flex items-center justify-center hover:border-[#CC2222] hover:text-[#CC2222] transition-all disabled:opacity-25 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setTeamIdx(i => Math.min(team.length - visibleCards, i + 1))}
                disabled={!canNext}
                data-testid="team-next-btn"
                className="w-11 h-11 border border-gray-300 flex items-center justify-center hover:border-[#CC2222] hover:text-[#CC2222] transition-all disabled:opacity-25 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
          <div className="overflow-hidden">
            <motion.div
              className="flex gap-4"
              animate={{ x: `-${teamIdx * (100 / visibleCards + 1.4)}%` }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              {team.map((m, i) => (
                <div
                  key={m.name}
                  data-testid={`team-card-${i}`}
                  className="group min-w-[calc(33.333%-11px)] bg-white border border-gray-200 hover:border-[#CC2222] transition-all duration-300 overflow-hidden"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={m.img}
                      alt={m.name}
                      className="w-full h-72 object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#181818]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="p-6">
                    <div className="w-8 h-[3px] bg-[#CC2222] mb-4" />
                    <h3 className="font-black text-[#111111] font-heading text-lg mb-1">{m.name}</h3>
                    <p className="text-[#CC2222] text-xs font-bold uppercase tracking-[0.15em] mb-2">{m.role}</p>
                    <p className="text-gray-500 text-sm">{m.expertise}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
      )}

      {/* ═══════════ 8. BLOG PREVIEW ═══════════ */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-14">
            <Reveal>
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">Insights & Knowledge</span>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-[#111111] font-heading">Blogs</h2>
            </Reveal>
            <Link
              to="/blog"
              data-testid="blog-view-all-btn"
              className="hidden md:inline-flex items-center gap-2 font-bold text-sm uppercase tracking-wider text-[#111111] hover:text-[#CC2222] group transition-colors"
            >
              All Articles <ArrowUpRight size={15} className="group-hover:rotate-12 transition-transform" />
            </Link>
          </div>

          {posts.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
              {[1, 2, 3].map(i => <div key={i} className="bg-[#F5F5F5] h-80 animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
              {posts.map((post, i) => (
                <Reveal key={post.id} delay={i * 0.1} data-testid={`blog-card-${i}`}>
                  <Link to={`/blog/${post.slug}`} className="group block bg-white border border-gray-200 hover:border-[#CC2222] transition-all duration-300 overflow-hidden h-full">
                    <div className="overflow-hidden">
                      <img
                        src={post.image_url || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80'}
                        alt={post.title}
                        className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500 grayscale-[30%] group-hover:grayscale-0"
                      />
                    </div>
                    <div className="p-7">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-[10px] font-black text-[#CC2222] uppercase tracking-[0.2em]">{post.category}</span>
                      </div>
                      <h3 className="font-black text-[#111111] font-heading text-lg leading-snug mb-3 group-hover:text-[#CC2222] transition-colors line-clamp-2">{post.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed mb-5 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center gap-1.5 text-[#CC2222] text-xs font-bold uppercase tracking-wide">
                        Read Article <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ═══════════ 9. FINAL CTA ═══════════ */}
      <section className="py-28 bg-[#181818]">        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#CC2222] mb-3 block">Work With Us</span>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading leading-tight">
                Ready for Your<br />Customized Gas Solutions?
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="space-y-6">
              <p className="text-white/50 text-lg leading-relaxed">
                From a single cylinder order to a complete air separation plant, our team is ready to design, build, and support your entire gas solution.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="cta-enquire-btn" className="group inline-flex items-center gap-2.5 bg-[#CC2222] text-white px-8 py-4 font-bold text-sm uppercase tracking-wider hover:bg-[#A81C1C] transition-colors">
                  Enquire Now <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/products" data-testid="cta-products-btn" className="inline-flex items-center gap-2 border border-white/20 text-white/70 px-8 py-4 font-bold text-sm uppercase tracking-wider hover:bg-white/5 hover:border-white/50 hover:text-white transition-all">
                  View Products
                </Link>
              </div>
              <div className="flex flex-wrap gap-6 pt-4 border-t border-white/10">
                {['+91 9894020509', 'info@sevagases.com', 'Coimbatore, Tamil Nadu'].map(info => (
                  <div key={info} className="flex items-center gap-2">
                    <span className="block w-1 h-1 bg-[#CC2222]" />
                    <span className="text-white/40 text-xs">{info}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <VideoWidget />
    </div>
  );
}
